import { Box, IconButton, keyframes, styled, useTheme } from '@mui/material';
import {
  LoadingIndicator,
  Typography,
} from '@forethought-technologies/forethought-elements';

import {
  DailyAudio,
  DailyProvider,
  useCallObject,
  useDaily,
  useDailyEvent,
} from '@daily-co/daily-react';

import { IconPhone, IconX } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { PopupModal } from 'components/PopupModal';
import { addEventMessage, setSipCallStatesByKey } from 'reducers/workflows';
import { useAppDispatch, useAppSelector } from 'hooks';
import { formatDuration, useDuration } from 'components/App/hooks/useDuration';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';
import { useUpdateConversation } from 'components/App/hooks';
import { selectSipCallState, selectConversationId } from 'reducers/workflows';
import type { ContextVariables } from 'api/types';
import ApiService from 'services/ApiService';

export const ZendeskSipCallDrawer = () => {
  const dispatch = useAppDispatch();
  const sipCallState = useAppSelector(selectSipCallState);
  const conversationId = useAppSelector(selectConversationId);
  const updateConversation = useUpdateConversation();

  const {
    isVisible,
    readyToCall = false,
    roomUrl = '',
    sipUri = '',
    token = '',
  } = sipCallState;

  const callObject = useCallObject({
    shouldCreateInstance: () => isVisible && readyToCall,
  });

  const onCallEnd = useCallback(() => {
    dispatch(
      setSipCallStatesByKey({
        isVisible: false,
        readyToCall: false,
        roomUrl: undefined,
        sipUri: undefined,
        token: undefined,
      }),
    );
    if (callObject) {
      callObject.leave()?.then(() => {
        return callObject.destroy();
      });
    }
  }, [callObject, dispatch]);

  if (!isVisible) {
    return null;
  }

  return (
    <PopupModal open={isVisible} position='bottom'>
      <DailyProvider callObject={callObject}>
        <SipCallComponent
          conversationId={conversationId}
          meetingToken={token}
          onCallEnd={onCallEnd}
          readyToCall={readyToCall}
          roomUrl={roomUrl}
          sipUri={sipUri}
          updateConversation={updateConversation}
        />
      </DailyProvider>
    </PopupModal>
  );
};

const CallDuration = ({ duration }: { duration: number }) => {
  return (
    <Box sx={{ visibility: !duration ? 'hidden' : 'visible' }}>
      <Typography variant='font14'>{formatDuration(duration)}</Typography>
    </Box>
  );
};

const SipCallComponent = ({
  conversationId,
  meetingToken,
  onCallEnd,
  readyToCall,
  roomUrl,
  sipUri,
  updateConversation,
}: {
  conversationId: string;
  meetingToken: string;
  onCallEnd: () => void;
  readyToCall: boolean;
  roomUrl: string;
  sipUri: string;
  updateConversation: (
    contextVariables: ContextVariables,
    options?: { isContinueAutoflowIntent?: boolean },
  ) => Promise<unknown>;
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const callObject = useDaily();
  // @ts-expect-error add callObject to window for debugging
  window.callObject = callObject;
  const [dialOutSessionId, setDialOutSessionId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const duration = useDuration(isConnected);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isConnecting || isConnected) {
        e.preventDefault();
        // Modern browsers will show a generic confirmation dialog
        return 'You have an ongoing call. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isConnecting, isConnected]);

  const end = useCallback(
    ({ addWidgetComponent = true }: { addWidgetComponent: boolean }) => {
      onCallEnd();
      if (addWidgetComponent) {
        if (dialOutSessionId) {
          dispatch(addEventMessage(`Call ended (${formatDuration(duration)})`));
        } else {
          dispatch(addEventMessage('Call canceled'));
        }
      }
    },
    [dialOutSessionId, dispatch, duration, onCallEnd],
  );

  const leaveRoom = useCallback(() => {
    if (!callObject) {
      return;
    }
    callObject
      .leave()
      ?.then(() => {
        return callObject.destroy();
      })
      .catch((err: unknown) => {
        sendErrorToSentry({
          consoleError: true,
          error: `Error leaving room: ${err}`,
        });
      });
  }, [callObject]);

  // Start dial-out to SIP URI
  const startDialOut = useCallback(() => {
    if (!callObject) {
      return;
    }

    callObject
      .join({
        startVideoOff: true,
        token: meetingToken,
        url: roomUrl,
      })
      .then(() => {
        callObject
          .startDialOut({
            displayName: 'SIP User',
            sipUri: sipUri,
            video: true,
          })
          .then(result => {
            setIsConnecting(false);
            setIsConnected(true);
            // Track the session ID if available in the result
            if (typeof result === 'object' && result?.session?.sessionId) {
              setDialOutSessionId(result.session.sessionId);
            }
          })
          .catch((err: unknown) => {
            sendErrorToSentry({
              consoleError: true,
              error: `Error starting dial-out: ${err}`,
            });
          });
      })
      .catch((err: unknown) => {
        sendErrorToSentry({
          consoleError: true,
          error: `Error joining room: ${err}`,
        });
      });
  }, [callObject, meetingToken, roomUrl, sipUri]);

  // Stop dial-out
  const stopDialOut = useCallback(() => {
    if (!callObject) {
      return;
    }

    if (!dialOutSessionId) {
      return;
    }

    callObject
      .stopDialOut({ sessionId: dialOutSessionId })
      .catch((err: unknown) => {
        sendErrorToSentry({
          additionalContext: { sessionId: dialOutSessionId },
          consoleError: true,
          error: `Error stopping dial-out: ${err}`,
        });
      })
      .finally(() => {
        end({ addWidgetComponent: true });
      });
  }, [callObject, dialOutSessionId, end]);

  const cleanupEndCall = useCallback(
    (e: { action?: string } | undefined) => {
      setIsConnected(false);
      setIsConnecting(false);
      leaveRoom();
      end({ addWidgetComponent: e?.action !== 'left-meeting' });
    },
    [leaveRoom, end],
  );

  // Handle call button click based on readyToCall state
  const handleCallButtonClick = useCallback(() => {
    if (!readyToCall) {
      // When not ready to call, trigger updateConversation
      updateConversation({});
      setIsConnecting(true);

      // Send call event when starting the call
      ApiService.request('send-sip-call-event', {
        urlValues: {
          callEventType: 'call_initiated',
          conversationId,
        },
      }).catch((err: unknown) => {
        sendErrorToSentry({
          consoleError: true,
          error: `Error sending call initiated event: ${err}`,
        });
      });
    } else if (isConnected) {
      // When connected, stop the call
      stopDialOut();
      // Send call event when ending the call
      ApiService.request('send-sip-call-event', {
        urlValues: {
          callEventType: 'call_ended',
          conversationId,
        },
      }).catch((err: unknown) => {
        sendErrorToSentry({
          consoleError: true,
          error: `Error sending call ended event: ${err}`,
        });
      });
    } else {
      // When ready to call but not connected, start the dial out
      setIsConnecting(true);
    }
  }, [
    readyToCall,
    isConnected,
    updateConversation,
    stopDialOut,
    conversationId,
  ]);

  useEffect(() => {
    if (isConnecting && roomUrl) {
      startDialOut();
    }
  }, [isConnecting, roomUrl, startDialOut]);

  useDailyEvent('left-meeting', cleanupEndCall);
  useDailyEvent('dialout-stopped', cleanupEndCall);

  return (
    <>
      <CallInterfaceContainer>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
          }}
        >
          <Typography variant='font24'>Call an agent</Typography>
          <IconButton
            onClick={() => {
              cleanupEndCall(undefined);
            }}
            sx={{ position: 'absolute', right: '16px' }}
          >
            <IconX size={20} />
          </IconButton>
        </Box>
        <ButtonWrapper>
          <CallDuration duration={duration} />
          <CallButton
            disabled={isConnecting}
            isCalling={isConnected}
            onClick={handleCallButtonClick}
          >
            {isConnecting ? (
              <LoadingIndicator color={theme.palette.colors.white} />
            ) : (
              <IconPhone
                color='white'
                size={40}
                style={{
                  transform: isConnected ? 'rotate(135deg)' : undefined,
                }}
              />
            )}
          </CallButton>
        </ButtonWrapper>
        <Box textAlign='center'>
          <Typography color={theme.palette.colors.grey[600]} variant='font14'>
            Please note that if you leave this page or refresh, the call would
            be disconnected.
          </Typography>
        </Box>
      </CallInterfaceContainer>
      <DailyAudio />
    </>
  );
};

const CustomizedIconButton = styled('button')`
  cursor: pointer;
  display: flex;
  width: 80px;
  height: 80px;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  flex-shrink: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid ${props => props.theme.palette.colors.slate[100]};

  &:hover {
    background: rgba(255, 255, 255, 0.4);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.03); }
`;

const CallButton = styled(CustomizedIconButton, {
  shouldForwardProp: prop => prop !== 'isCalling',
})<{ isCalling: boolean }>(({ isCalling, theme }) => ({
  '&:hover': {
    backgroundColor: isCalling
      ? theme.palette.colors.red[400]
      : theme.palette.colors.green[400],
  },

  animation: isCalling ? 'none' : `${pulse} 2s ease-in-out infinite`,

  backgroundColor: isCalling
    ? theme.palette.colors.red[500]
    : theme.palette.colors.green[500],
}));

const CallInterfaceContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
`;

const ButtonWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
  align-items: center;
  width: 80%;
`;
